import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

// Retry wrapper for API calls
const apiWithRetry = async (fn, maxRetries = 2) => {
  for (let i = 0; i <= maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      if (i === maxRetries || (error.response && error.response.status < 500)) {
        throw error;
      }
      // Wait before retry (exponential backoff)
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await apiWithRetry(() => axios.get(`${API}/auth/me`));
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (email, password, totpCode = null) => {
    const payload = { email, password };
    if (totpCode) payload.totp_code = totpCode;
    
    const response = await apiWithRetry(() => axios.post(`${API}/auth/login`, payload));
    const data = response.data;
    
    // If 2FA is required
    if (data.requires_2fa && !data.access_token) {
      return { requires_2fa: true };
    }
    
    const { access_token, user: userData } = data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setToken(access_token);
    setUser(userData);
    
    return { ...userData, requires_enrollment: data.requires_enrollment || false, must_setup_2fa: data.must_setup_2fa || false };
  };

  const register = async (email, name, password, role) => {
    const response = await apiWithRetry(() => axios.post(`${API}/auth/register`, { email, name, password, role }));
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    
    setToken(access_token);
    setUser(userData);
    
    return userData;
  };

  const setSession = (access_token, userData) => {
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setToken(null);
    setUser(null);
  };

  const canApproveRisk = (riskLevel) => {
    if (!user) return false;
    if (user.role === 'admin') return true;
    if (user.role === 'senior_admin') return ['low', 'medium', 'high', 'critical'].includes(riskLevel);
    if (user.role === 'junior_admin') return ['low', 'medium'].includes(riskLevel);
    return false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      register, 
      logout,
      setSession,
      canApproveRisk,
      isAuthenticated: !!user 
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
